/**
 * 表情包商店管理页面 (Admin)
 */
import { useState, useEffect } from 'react';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { SiteHeader } from '@/components/SiteHeder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Smile,
  Plus,
  Trash2,
  Upload,
  RefreshCw,
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  FileArchive,
  HelpCircle,
  Info,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Pencil,
  Pin,
  PinOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { marketApi, type StickerPackItem, type StickerItem } from '@/services/marketApi';

export function StickerManagementPage() {
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState<StickerPackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  // 上传 .alitapack 弹窗
  const [uploadPackOpen, setUploadPackOpen] = useState(false);
  const [packFile, setPackFile] = useState<File | null>(null);
  const [uploadingPack, setUploadingPack] = useState(false);

  // 批量在线建包弹窗
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [showBatchGuide, setShowBatchGuide] = useState(false);
  const [batchId, setBatchId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [batchStyle, setBatchStyle] = useState('');
  const [batchAccessLevel, setBatchAccessLevel] = useState('free');
  const [batchStickers, setBatchStickers] = useState<
    Array<{ file?: File; filePath?: string; label: string; meaning: string; keywords: string; previewUrl?: string }>
  >([]);
  const [creatingBatch, setCreatingBatch] = useState(false);

  // 预览详情弹窗
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState<StickerPackItem | null>(null);
  const [previewStickers, setPreviewStickers] = useState<StickerItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // 编辑内容弹窗（包元数据 + 单张表情释义修正）
  const [editOpen, setEditOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<StickerPackItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editStyle, setEditStyle] = useState('');
  const [editAccess, setEditAccess] = useState('free');
  const [editStickers, setEditStickers] = useState<
    Array<{ id: string; file_url: string; thumb_url: string; label: string; meaning: string; keywordsText: string }>
  >([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadPacks = async () => {
    setLoading(true);
    try {
      const res = await marketApi.getAdminStickers({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: status !== 'all' ? status : undefined,
      });
      if (res.success && res.data) {
        setPacks(res.data.items || []);
        setTotal(res.data.total || 0);
      } else {
        toast.error(res.message || '加载表情包列表失败');
      }
    } catch (err: any) {
      toast.error(err.message || '加载表情包列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPacks();
  }, [page, status]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadPacks();
  };

  const handleUploadPack = async () => {
    if (!packFile) {
      toast.error('请选择 .alitapack 或 .zip 文件');
      return;
    }
    setUploadingPack(true);
    try {
      const res = await marketApi.uploadAlitaPack(packFile);
      if (res.success) {
        toast.success('表情包上传并解析成功');
        setUploadPackOpen(false);
        setPackFile(null);
        loadPacks();
      } else {
        toast.error(res.message || '上传解析失败');
      }
    } catch (err: any) {
      toast.error(err.message || '上传解析失败');
    } finally {
      setUploadingPack(false);
    }
  };

  const handleBatchImageSelect = (files: FileList | null) => {
    if (!files) return;
    const newItems = Array.from(files).map((file, idx) => {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      return {
        file,
        label: nameWithoutExt || `表情${idx + 1}`,
        meaning: nameWithoutExt || `表情${idx + 1}`,
        keywords: nameWithoutExt,
        previewUrl: URL.createObjectURL(file),
      };
    });
    setBatchStickers((prev) => [...prev, ...newItems]);
  };

  const handleCreateBatchPack = async () => {
    if (!batchId || !batchName) {
      toast.error('请填写表情包 ID 和名称');
      return;
    }
    if (batchStickers.length === 0) {
      toast.error('请至少选择一张表情图片');
      return;
    }

    setCreatingBatch(true);
    try {
      // 1. 先上传所有图片
      const filesToUpload = batchStickers.map((item) => item.file!).filter(Boolean);
      const uploadRes = await marketApi.uploadImages(filesToUpload);
      if (!uploadRes.success || !uploadRes.data?.urls) {
        toast.error('上传表情图片失败: ' + (uploadRes.message || '未知错误'));
        setCreatingBatch(false);
        return;
      }

      const uploadedUrls = uploadRes.data.urls;

      // 2. 调用打包创建接口
      const stickersPayload = batchStickers.map((item, idx) => ({
        file_path: uploadedUrls[idx] || '',
        label: item.label,
        meaning: item.meaning || item.label,
        keywords: item.keywords ? item.keywords.split(/[,， ]+/).filter(Boolean) : [],
      }));

      const res = await marketApi.createBatchStickerPack({
        id: batchId,
        name: batchName,
        style: batchStyle || '日常搞怪',
        access_level: batchAccessLevel,
        stickers: stickersPayload,
      });

      if (res.success) {
        toast.success('表情包创建并自动打包成功！');
        setCreateBatchOpen(false);
        setBatchId('');
        setBatchName('');
        setBatchStyle('');
        setBatchStickers([]);
        loadPacks();
      } else {
        toast.error(res.message || '创建失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleOpenPreview = async (item: StickerPackItem) => {
    setSelectedPack(item);
    setPreviewOpen(true);
    setLoadingDetail(true);
    try {
      const res = await marketApi.getMarketStickerDetail(item.id);
      if (res.success && res.data) {
        setPreviewStickers(res.data.stickers || []);
      }
    } catch (err) {
      // ignore
    } finally {
      setLoadingDetail(false);
    }
  };

  // 打开编辑内容弹窗（风格标签 / 名称 / 权限 + 单张表情释义）
  const handleOpenEdit = async (item: StickerPackItem) => {
    setEditingPack(item);
    setEditName(item.name);
    setEditStyle(item.style || '');
    setEditAccess(item.access_level || 'free');
    setEditStickers([]);
    setEditOpen(true);
    setLoadingEdit(true);
    try {
      const res = await marketApi.getMarketStickerDetail(item.id);
      if (res.success && res.data?.stickers) {
        setEditStickers(
          res.data.stickers.map((st) => ({
            id: st.id,
            file_url: st.file_url,
            thumb_url: st.thumb_url || st.file_url,
            label: st.label,
            meaning: st.meaning || '',
            keywordsText: (st.keywords || []).join(', '),
          }))
        );
      } else {
        toast.error(res.message || '加载表情明细失败');
      }
    } catch (err: any) {
      toast.error(err.message || '加载表情明细失败');
    } finally {
      setLoadingEdit(false);
    }
  };

  // 保存内容修正：服务端自动重新打包 .alitapack 并递增 version_code
  const handleSaveEdit = async () => {
    if (!editingPack) return;
    if (!editName.trim()) {
      toast.error('表情包名称不能为空');
      return;
    }
    setSavingEdit(true);
    try {
      const res = await marketApi.updateStickerPackContent(editingPack.id, {
        name: editName.trim(),
        style: editStyle.trim(),
        access_level: editAccess,
        stickers: editStickers.map((st) => ({
          id: st.id,
          label: st.label.trim(),
          meaning: st.meaning.trim(),
          keywords: st.keywordsText.split(/[,， ]+/).filter(Boolean),
        })),
      });
      if (res.success) {
        toast.success('已保存并重新打包，版本号已自动递增，已安装用户可检测到更新');
        setEditOpen(false);
        loadPacks();
      } else {
        toast.error(res.message || '保存失败');
      }
    } catch (err: any) {
      toast.error(err.message || '保存失败');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleStatus = async (item: StickerPackItem) => {
    const nextStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      const res = await marketApi.updateStickerPackStatus(item.id, nextStatus);
      if (res.success) {
        toast.success(`已${nextStatus === 'published' ? '上架' : '下架'}`);
        loadPacks();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  // 投稿审核：通过上架 / 驳回
  const handleReview = async (item: StickerPackItem, approve: boolean) => {
    try {
      const res = await marketApi.updateStickerPackStatus(item.id, approve ? 'published' : 'rejected');
      if (res.success) {
        toast.success(approve ? '已通过并上架' : '已驳回');
        loadPacks();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  const handleDeletePack = async (id: string) => {
    try {
      const res = await marketApi.deleteStickerPack(id);
      if (res.success) {
        toast.success('删除成功');
        loadPacks();
      } else {
        toast.error(res.message || '删除失败');
      }
    } catch (err: any) {
      toast.error(err.message || '删除失败');
    }
  };

  // 置顶 / 取消置顶：置顶包在商店列表中排最前
  const handleTogglePin = async (item: StickerPackItem) => {
    try {
      const res = await marketApi.updateStickerPackPin(item.id, !item.pinned);
      if (res.success) {
        toast.success(res.data?.pinned ? '已置顶，商店列表中将排最前' : '已取消置顶');
        loadPacks();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader title="表情包管理" />
        <div className="flex flex-1 flex-col gap-4 p-6">
          {/* 页面标题栏 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Smile className="w-6 h-6 text-pink-600" />
                表情包商店管理 (Sticker Store)
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                管理与上架 Alita AI 聊天表情包，支持 .alitapack 自动解包、在线多图批量打包与 AI 情绪语义配置
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setUploadPackOpen(true)}>
                <FileArchive className="w-4 h-4 mr-2" />
                上传 .alitapack 压缩包
              </Button>
              <Button onClick={() => setCreateBatchOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                在线批量上传打包
              </Button>
            </div>
          </div>

          {/* 搜索过滤栏 */}
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="搜索表情包 ID、名称、风格..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" variant="secondary">搜索</Button>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="状态" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部状态</SelectItem>
                      <SelectItem value="published">已发布</SelectItem>
                      <SelectItem value="draft">草稿</SelectItem>
                      <SelectItem value="pending_review">待审核</SelectItem>
                      <SelectItem value="rejected">已驳回</SelectItem>
                      <SelectItem value="archived">已归档</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button variant="ghost" size="icon" onClick={() => loadPacks()} title="刷新">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* 表格列表 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>表情包列表</CardTitle>
              <CardDescription>共 {total} 个表情包</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">封面</TableHead>
                      <TableHead>表情包名称 / ID</TableHead>
                      <TableHead>风格标签</TableHead>
                      <TableHead>张数 / 大小</TableHead>
                      <TableHead>版本 / 权限</TableHead>
                      <TableHead>好用度与评分</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead>下载量</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                          {loading ? '加载中...' : '暂无表情包数据'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      packs.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden border">
                              {item.cover_url ? (
                                <img src={item.cover_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <Smile className="w-6 h-6 text-muted-foreground" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div
                                className="font-semibold flex items-center gap-1.5 cursor-pointer hover:text-purple-600 transition-colors"
                                onClick={() => handleOpenPreview(item)}
                                title="点击查看详情与动图预览"
                              >
                                <span>{item.name}</span>
                                {item.is_official && (
                                  <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                                    官方
                                  </Badge>
                                )}
                                {item.pinned && (
                                  <Badge className="text-[10px] bg-amber-500 text-white px-1.5 py-0 gap-0.5">
                                    <Pin className="w-2.5 h-2.5" />
                                    置顶
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mt-0.5">{item.id}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className="cursor-pointer hover:bg-secondary/80 transition-colors"
                              title="点击修改风格标签与表情释义"
                              onClick={() => handleOpenEdit(item)}
                            >
                              {item.style || '表情'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs font-medium">{item.sticker_count} 张</div>
                            <div className="text-[11px] text-muted-foreground">
                              {(item.file_size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              <span className="text-xs font-mono font-medium">v{item.version}</span>
                              {item.access_level === 'vip' ? (
                                <Badge className="bg-amber-500 text-white text-[10px]">VIP 专享</Badge>
                              ) : (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 text-[10px]">免费</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5">
                                <Badge className="bg-pink-600 text-white text-[10px] px-1.5 py-0">
                                  {item.quality_score ?? 80} 分
                                </Badge>
                                <span className="text-xs text-amber-600 font-medium">
                                  ★ {(item.star_rating ?? 4.0).toFixed(1)}
                                </span>
                              </div>
                              <span className="text-[11px] text-muted-foreground">
                                {item.rating_count ?? 0} 人评价
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.status === 'published' ? (
                              <Badge className="bg-emerald-600">已发布</Badge>
                            ) : item.status === 'draft' ? (
                              <Badge variant="secondary">草稿</Badge>
                            ) : item.status === 'pending_review' ? (
                              <Badge className="bg-amber-500">待审核</Badge>
                            ) : item.status === 'rejected' ? (
                              <Badge className="bg-red-600">已驳回</Badge>
                            ) : (
                              <Badge variant="outline">已归档</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-medium">{item.download_count}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {item.status === 'pending_review' && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2 gap-1 text-emerald-700 border-emerald-200 bg-emerald-50/70 hover:bg-emerald-100 hover:text-emerald-800"
                                    onClick={() => handleReview(item, true)}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    <span>通过</span>
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2 gap-1 text-red-700 border-red-200 bg-red-50/70 hover:bg-red-100 hover:text-red-800"
                                    onClick={() => handleReview(item, false)}
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                    <span>驳回</span>
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs px-2 gap-1 text-blue-700 border-blue-200 bg-blue-50/70 hover:bg-blue-100 hover:text-blue-800"
                                onClick={() => handleOpenEdit(item)}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                <span>编辑内容</span>
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs px-2 gap-1 text-purple-700 border-purple-200 bg-purple-50/70 hover:bg-purple-100 hover:text-purple-800"
                                onClick={() => handleOpenPreview(item)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span>查看详情</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleTogglePin(item)}
                                title={item.pinned ? '取消置顶' : '置顶（商店列表排最前）'}
                              >
                                {item.pinned ? (
                                  <PinOff className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <Pin className="w-4 h-4 text-slate-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleToggleStatus(item)}
                                title={item.status === 'published' ? '下架' : '发布'}
                              >
                                {item.status === 'published' ? (
                                  <XCircle className="w-4 h-4 text-orange-500" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                )}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-7 px-2" title="删除">
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>确认删除表情包？</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      此操作将永久删除表情包「{item.name}」({item.id}) 及其所有动图资源。
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>取消</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeletePack(item.id)}
                                      className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                      确认删除
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 上传 .alitapack 压缩包 Dialog */}
        <Dialog open={uploadPackOpen} onOpenChange={setUploadPackOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>上传 .alitapack 表情包</DialogTitle>
              <DialogDescription>
                上传由 Android 客户端导出或标准打包的 .alitapack / .zip 文件（内含 pack_manifest.json 与动图）
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="space-y-1.5">
                <Label>选择 .alitapack / .zip 压缩包</Label>
                <Input
                  type="file"
                  accept=".alitapack,.zip"
                  onChange={(e) => setPackFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadPackOpen(false)}>取消</Button>
              <Button onClick={handleUploadPack} disabled={uploadingPack || !packFile}>
                {uploadingPack ? '正在解包上传...' : '确认上传'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 在线批量上传图片并打包 Dialog */}
        <Dialog open={createBatchOpen} onOpenChange={setCreateBatchOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>在线批量上传表情并自动打包</DialogTitle>
              <DialogDescription>
                批量选择 GIF/PNG/WebP 图片，配置每张表情的名称与情绪含义，系统将自动打包为 .alitapack
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="batch_id">表情包 ID (唯一标识)</Label>
                  <Input
                    id="batch_id"
                    placeholder="如: alita_daily"
                    value={batchId}
                    onChange={(e) => setBatchId(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="batch_name">表情包名称</Label>
                  <Input
                    id="batch_name"
                    placeholder="如: Alita 日常表情包"
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="batch_style">风格描述 (供 AI 挑图参考)</Label>
                  <Input
                    id="batch_style"
                    placeholder="如: 可爱搞怪 / 日常聊天"
                    value={batchStyle}
                    onChange={(e) => setBatchStyle(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>权限等级</Label>
                  <Select value={batchAccessLevel} onValueChange={setBatchAccessLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">全员免费 (free)</SelectItem>
                      <SelectItem value="vip">VIP 专享 (vip)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 批量选图 */}
              <div className="space-y-2 border-2 border-dashed rounded-lg p-4 text-center">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                <div className="text-sm font-medium">选择或拖拽表情图片到此处</div>
                <div className="text-xs text-muted-foreground">支持批量多选 GIF / WebP / PNG / JPG 图片</div>
                <Input
                  type="file"
                  multiple
                  accept="image/gif,image/webp,image/png,image/jpeg"
                  className="max-w-xs mx-auto mt-2"
                  onChange={(e) => handleBatchImageSelect(e.target.files)}
                />
              </div>

              {/* 表情明细配置列表 */}
              {batchStickers.length > 0 && (
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">已选表情 ({batchStickers.length} 张)</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1 text-blue-600 border-blue-200 bg-blue-50/50 hover:bg-blue-100"
                        onClick={() => setShowBatchGuide(!showBatchGuide)}
                      >
                        <Info className="w-3.5 h-3.5" />
                        <span>{showBatchGuide ? '收起填写指南' : '💡 字段作用与填写指南'}</span>
                        {showBatchGuide ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </Button>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-red-600" onClick={() => setBatchStickers([])}>
                      清空全部
                    </Button>
                  </div>

                  {/* 折叠式指南卡片 */}
                  {showBatchGuide && (
                    <div className="p-3 bg-blue-50/70 border border-blue-200/80 rounded-lg text-xs space-y-2 text-slate-700 animate-in fade-in-50 duration-150">
                      <div className="font-semibold text-blue-900 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                        表情配置 3 个字段的作用说明与示例：
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-1">
                        <div className="bg-white/90 p-2 rounded border border-blue-100 shadow-2xs">
                          <div className="font-semibold text-blue-800 flex items-center gap-1 mb-0.5">
                            <Badge variant="outline" className="px-1 py-0 text-[10px] bg-blue-50 text-blue-700 border-blue-200">1</Badge>
                            表情名称 (label)
                          </div>
                          <p className="text-slate-600 leading-snug">
                            <strong>作用</strong>：客户端表情键盘、详情卡片上展示的标题。<br/>
                            <span className="text-muted-foreground">示例：<code className="bg-slate-100 px-1 rounded text-[11px]">打住</code>、<code className="bg-slate-100 px-1 rounded text-[11px]">给你点赞</code></span>
                          </p>
                        </div>

                        <div className="bg-white/90 p-2 rounded border border-purple-100 shadow-2xs">
                          <div className="font-semibold text-purple-800 flex items-center gap-1 mb-0.5">
                            <Badge variant="outline" className="px-1 py-0 text-[10px] bg-purple-50 text-purple-700 border-purple-200">2</Badge>
                            情绪语义 (meaning)
                          </div>
                          <p className="text-slate-600 leading-snug">
                            <strong>作用</strong>：AI 对话时理解语境并触发该表情的核心语义。<br/>
                            <span className="text-muted-foreground">示例：<code className="bg-slate-100 px-1 rounded text-[11px]">表达认可、赞同或鼓励的情绪</code></span>
                          </p>
                        </div>

                        <div className="bg-white/90 p-2 rounded border border-amber-100 shadow-2xs">
                          <div className="font-semibold text-amber-800 flex items-center gap-1 mb-0.5">
                            <Badge variant="outline" className="px-1 py-0 text-[10px] bg-amber-50 text-amber-700 border-amber-200">3</Badge>
                            检索关键词 (keywords)
                          </div>
                          <p className="text-slate-600 leading-snug">
                            <strong>作用</strong>：用户在表情搜框输入或输入法打字快捷联想。<br/>
                            <span className="text-muted-foreground">示例：<code className="bg-slate-100 px-1 rounded text-[11px]">点赞, 夸奖, 牛, 厉害</code></span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 表头提示 + Tooltip 悬浮帮助 */}
                  <TooltipProvider delayDuration={100}>
                    <div className="hidden md:flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/60 rounded-md border">
                      <span className="w-12 text-center flex-shrink-0">缩略图</span>
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        {/* 列 1 */}
                        <div className="flex items-center gap-1">
                          <span>表情名称 (label)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-muted-foreground hover:text-blue-600 cursor-help inline-flex items-center">
                                <HelpCircle className="w-3.5 h-3.5 text-blue-500" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs p-2.5 text-xs bg-popover text-popover-foreground shadow-lg border">
                              <p className="font-semibold text-blue-600 mb-1">🏷️ 表情名称 (UI 标题)</p>
                              <p className="text-muted-foreground leading-relaxed">
                                <strong>实际作用</strong>：在表情选择面板、详情页中展示给用户看的标题。<br/>
                                <strong>建议</strong>：简短清晰（如：<code className="bg-muted px-1 rounded">暂停</code>、<code className="bg-muted px-1 rounded">点赞</code>）。
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        {/* 列 2 */}
                        <div className="flex items-center gap-1">
                          <span>情绪语义 (meaning)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-muted-foreground hover:text-purple-600 cursor-help inline-flex items-center">
                                <HelpCircle className="w-3.5 h-3.5 text-purple-500" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs p-2.5 text-xs bg-popover text-popover-foreground shadow-lg border">
                              <p className="font-semibold text-purple-600 mb-1">🤖 情绪语义 (AI 匹配核心)</p>
                              <p className="text-muted-foreground leading-relaxed">
                                <strong>实际作用</strong>：供大模型在语音/文字对话中感知语境意图。当 AI 判定适合当前情绪时优先自动发送该表情。<br/>
                                <strong>建议</strong>：写明动作或情绪含义（如：<code className="bg-muted px-1 rounded">示意打住或等待一下的搞怪表情</code>）。
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        {/* 列 3 */}
                        <div className="flex items-center gap-1">
                          <span>检索关键词 (keywords)</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-muted-foreground hover:text-amber-600 cursor-help inline-flex items-center">
                                <HelpCircle className="w-3.5 h-3.5 text-amber-500" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs p-2.5 text-xs bg-popover text-popover-foreground shadow-lg border">
                              <p className="font-semibold text-amber-600 mb-1">🔍 检索关键词 (快捷联想)</p>
                              <p className="text-muted-foreground leading-relaxed">
                                <strong>实际作用</strong>：在表情面板搜索框或输入法快捷联想时匹配的词。<br/>
                                <strong>建议</strong>：多个关键词用逗号隔开（如：<code className="bg-muted px-1 rounded">暂停, 稍等, 等等, 别急</code>）。
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                      <span className="w-8 text-center flex-shrink-0">操作</span>
                    </div>
                  </TooltipProvider>

                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {batchStickers.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 border rounded-lg p-2.5 bg-muted/20 hover:bg-muted/30 transition-colors">
                        <div className="w-12 h-12 rounded bg-muted flex-shrink-0 overflow-hidden border">
                          <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                          <Input
                            placeholder="表情名称 (如: 暂停)"
                            value={item.label}
                            onChange={(e) => {
                              const updated = [...batchStickers];
                              updated[idx].label = e.target.value;
                              setBatchStickers(updated);
                            }}
                          />
                          <Input
                            placeholder="情绪语义 (如: 示意等待或打住)"
                            value={item.meaning}
                            onChange={(e) => {
                              const updated = [...batchStickers];
                              updated[idx].meaning = e.target.value;
                              setBatchStickers(updated);
                            }}
                          />
                          <Input
                            placeholder="关键词 (如: 暂停, 等等, 稍等)"
                            value={item.keywords}
                            onChange={(e) => {
                              const updated = [...batchStickers];
                              updated[idx].keywords = e.target.value;
                              setBatchStickers(updated);
                            }}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:text-red-600"
                          onClick={() => setBatchStickers(batchStickers.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateBatchOpen(false)}>取消</Button>
              <Button onClick={handleCreateBatchPack} disabled={creatingBatch || batchStickers.length === 0}>
                {creatingBatch ? '正在上传并压缩打包...' : '开始自动打包上架'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 编辑内容 Dialog（风格标签 / 名称 / 权限 + 单张表情释义修正） */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Pencil className="w-5 h-5 text-blue-600" />
                <span>编辑表情包 - {editingPack?.name}</span>
              </DialogTitle>
              <DialogDescription>
                在线修正风格标签与单张表情释义，保存后自动重新打包 .alitapack 并递增版本号，无需整包重传
              </DialogDescription>
            </DialogHeader>

            {loadingEdit ? (
              <div className="py-12 text-center text-muted-foreground">正在加载表情明细...</div>
            ) : (
              <div className="grid gap-4 py-2">
                {/* 包级元数据 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit_pack_name">表情包名称</Label>
                    <Input
                      id="edit_pack_name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit_pack_style">风格标签 (供 AI 挑图参考)</Label>
                    <Input
                      id="edit_pack_style"
                      placeholder="如: 情侣/猫猫、可爱搞怪"
                      value={editStyle}
                      onChange={(e) => setEditStyle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>权限等级</Label>
                    <Select value={editAccess} onValueChange={setEditAccess}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">全员免费 (free)</SelectItem>
                        <SelectItem value="vip">VIP 专享 (vip)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* 单张表情释义修正 */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">表情释义修正 ({editStickers.length} 张)</span>
                    <span className="text-xs text-muted-foreground">
                      修改情绪语义 / 关键词后保存即可生效，已安装用户将收到更新提示
                    </span>
                  </div>
                  <div className="hidden md:flex items-center gap-3 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/60 rounded-md border">
                    <span className="w-12 text-center flex-shrink-0">缩略图</span>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <span>表情名称 (label)</span>
                      <span>情绪语义 (meaning，AI 匹配核心)</span>
                      <span>检索关键词 (逗号分隔)</span>
                    </div>
                  </div>
                  <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                    {editStickers.map((st, idx) => (
                      <div
                        key={st.id}
                        className="flex items-center gap-3 border rounded-lg p-2.5 bg-muted/20 hover:bg-muted/30 transition-colors"
                      >
                        <div className="w-12 h-12 rounded bg-muted flex-shrink-0 overflow-hidden border">
                          <img src={st.thumb_url || st.file_url} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                          <Input
                            placeholder="表情名称"
                            value={st.label}
                            onChange={(e) => {
                              const updated = [...editStickers];
                              updated[idx].label = e.target.value;
                              setEditStickers(updated);
                            }}
                          />
                          <Input
                            placeholder="情绪语义 (如: 示意等待或打住)"
                            value={st.meaning}
                            onChange={(e) => {
                              const updated = [...editStickers];
                              updated[idx].meaning = e.target.value;
                              setEditStickers(updated);
                            }}
                          />
                          <Input
                            placeholder="关键词 (如: 暂停, 等等, 稍等)"
                            value={st.keywordsText}
                            onChange={(e) => {
                              const updated = [...editStickers];
                              updated[idx].keywordsText = e.target.value;
                              setEditStickers(updated);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>取消</Button>
              <Button onClick={handleSaveEdit} disabled={savingEdit || loadingEdit}>
                {savingEdit ? '正在保存并重新打包...' : '保存并重新打包'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 详情与动图预览 Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Smile className="w-5 h-5 text-pink-600" />
                <span>{selectedPack?.name}</span>
                <Badge variant="secondary" className="ml-1 text-xs">{selectedPack?.style || '表情'}</Badge>
                {selectedPack?.is_official && (
                  <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">官方</Badge>
                )}
                {selectedPack?.access_level === 'vip' ? (
                  <Badge className="bg-amber-500 text-white text-[10px]">VIP 专享</Badge>
                ) : (
                  <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 text-[10px]">免费</Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                表情包 ID: <code className="font-mono bg-muted px-1 py-0.5 rounded text-xs">{selectedPack?.id}</code> · 版本 v{selectedPack?.version} · 共 {previewStickers.length} 张表情
              </DialogDescription>
            </DialogHeader>

            {/* 表情包基本信息概览卡片 */}
            {selectedPack && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3 bg-muted/40 rounded-lg border text-xs">
                <div>
                  <span className="text-muted-foreground">好用度指数：</span>
                  <span className="font-semibold text-pink-600 ml-1">{selectedPack.quality_score ?? 80} 分</span>
                </div>
                <div>
                  <span className="text-muted-foreground">口碑评分：</span>
                  <span className="font-semibold text-amber-600 ml-1">★ {(selectedPack.star_rating ?? 4.0).toFixed(1)} ({selectedPack.rating_count ?? 0} 人)</span>
                </div>
                <div>
                  <span className="text-muted-foreground">包文件大小：</span>
                  <span className="font-medium ml-1">{(selectedPack.file_size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                <div>
                  <span className="text-muted-foreground">累计下载：</span>
                  <span className="font-medium ml-1">{selectedPack.download_count} 次</span>
                </div>
              </div>
            )}

            {loadingDetail ? (
              <div className="py-12 text-center text-muted-foreground">正在加载表情详情...</div>
            ) : (
              <div className="space-y-3">
                <div className="text-xs font-semibold text-muted-foreground flex items-center justify-between">
                  <span>表情明细清单 ({previewStickers.length} 张)：</span>
                  <span className="text-[11px]">点击图片可在新窗口查看原图</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 py-1">
                  {previewStickers.map((st) => (
                    <div
                      key={st.id}
                      className="border rounded-lg p-3 flex flex-col items-center justify-between text-center bg-card hover:shadow-md transition-all group"
                    >
                      <a
                        href={st.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="w-20 h-20 rounded-md overflow-hidden flex items-center justify-center bg-muted/30 border p-1 group-hover:border-purple-300 transition-colors"
                        title="点击查看原图动图"
                      >
                        <img src={st.file_url} alt={st.label} className="max-w-full max-h-full object-contain" />
                      </a>
                      <div className="w-full mt-2 space-y-1 text-left">
                        <div className="text-xs font-semibold text-slate-800 text-center truncate">{st.label}</div>
                        {st.meaning && (
                          <div className="text-[11px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded line-clamp-2" title={st.meaning}>
                            <span className="text-purple-600 font-medium">语义: </span>{st.meaning}
                          </div>
                        )}
                        {st.keywords && st.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 justify-center">
                            {st.keywords.slice(0, 3).map((kw, kIdx) => (
                              <span key={kIdx} className="text-[10px] bg-slate-100 text-slate-600 px-1 rounded">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
